/**
 * DEV-258 — sessão, definição e respostas de uma descrição de vaga. Sem
 * sessão válida (ou 401 do gateway) a pessoa volta ao Onboarding.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlignmentApiError, alignmentApi } from './api';
import { clearAlignmentSession, loadAlignmentSession } from './session';

export const definitionQueryKey = (jobDescriptionId: string) =>
  ['alignment-definition', jobDescriptionId] as const;
export const descriptionQueryKey = (jobDescriptionId: string) =>
  ['alignment-description', jobDescriptionId] as const;

export type FormSessionFailure = 'offline' | 'grave' | null;

export function useFormSession(tenant: string, formId: string) {
  const navigate = useNavigate();
  const session = useMemo(() => loadAlignmentSession(formId, new Date()), [formId]);
  const token = session?.sessionToken ?? '';
  const jd = session?.jobDescriptionId ?? '';

  const definition = useQuery({
    queryKey: definitionQueryKey(jd),
    queryFn: ({ signal }) => alignmentApi.getDefinition(token, signal),
    enabled: !!session,
    staleTime: Infinity,
    retry: false,
  });
  const description = useQuery({
    queryKey: descriptionQueryKey(jd),
    queryFn: ({ signal }) => alignmentApi.getDescription(token, signal),
    enabled: !!session,
    staleTime: Infinity,
    retry: false,
  });

  const error = definition.error ?? description.error;
  const expired =
    !session || (error instanceof AlignmentApiError && error.code === 'session_expired');

  useEffect(() => {
    if (!expired) return;
    clearAlignmentSession(formId);
    void navigate({
      to: '/descricao-vaga/$tenant/$formId',
      params: { tenant, formId },
      replace: true,
    });
  }, [expired, formId, navigate, tenant]);

  const failure: FormSessionFailure =
    error && !expired
      ? error instanceof AlignmentApiError && error.status === 0
        ? 'offline'
        : 'grave'
      : null;

  return {
    session,
    definition: definition.data,
    description: description.data,
    loading: !expired && !failure && (!definition.data || !description.data),
    failure,
    expired,
    retry: () => {
      void definition.refetch();
      void description.refetch();
    },
  };
}
